import { Box, Typography, Button, CircularProgress } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { RxCross2 } from "react-icons/rx";
import { Modal } from "@mui/material";
import { selectUser } from "../../../store/slice/user";
import { currencyJson } from "../../../lib/helper";
import { COLORS, RADIUS, fontFamilyFor } from "../../../theme";

const SEATSIO_REGION = process.env.REACT_APP_SEATSIO_REGION || "eu";
const SEATSIO_PUBLIC_KEY = process.env.REACT_APP_SEATSIO_PUBLIC_KEY || "";
const CDN_URL = `https://cdn-${SEATSIO_REGION}.seatsio.net/chart.js`;

function loadSeatsioScript() {
  return new Promise((resolve, reject) => {
    if (window.seatsio) {
      resolve();
      return;
    }
    const existing = document.querySelector(`script[src="${CDN_URL}"]`);
    if (existing) {
      existing.addEventListener("load", resolve);
      existing.addEventListener("error", reject);
      return;
    }
    const script = document.createElement("script");
    script.src = CDN_URL;
    script.async = true;
    script.onload = resolve;
    script.onerror = reject;
    document.head.appendChild(script);
  });
}

const SeatingMap = ({ open, setOpen, data }) => {
  const { t } = useTranslation();
  const { language } = useSelector(selectUser);
  const navigate = useNavigate();
  const chartRef = useRef(null);
  const containerRef = useRef(null);
  const [selectedSeats, setSelectedSeats] = useState([]);
  const [holdToken, setHoldToken] = useState(null);
  const [scriptReady, setScriptReady] = useState(false);
  const [chartError, setChartError] = useState(null);
  const isArabic = language === "ar";

  const tickets = data?.tickets || [];
  const seatingEventKey = data?.seating_event_key;

  const ticketByCategory = {};
  tickets.forEach((t) => {
    // Prefer explicit seat_category_key, fall back to translation name
    const key = t?.seat_category_key || t?.translation?.en?.name || t?.translation?.ar?.name || "";
    if (key) {
      ticketByCategory[key.toLowerCase()] = t;
    }
  });

  const pricing = tickets.map((ticket) => {
    const category = ticket?.seat_category_key || ticket?.translation?.en?.name || ticket?.translation?.ar?.name || "";
    return {
      category,
      price: ticket.price,
    };
  });

  const currency = tickets[0]?.currency || "KWD";

  const totalPrice = selectedSeats.reduce((sum, s) => sum + (s.price || 0), 0);

  useEffect(() => {
    if (!open) {
      setScriptReady(false);
      return;
    }
    loadSeatsioScript()
      .then(() => setScriptReady(true))
      .catch(() => setChartError("Failed to load seating chart library."));
  }, [open]);

  useEffect(() => {
    if (!open) {
      setChartError(null);
      return;
    }
    if (!seatingEventKey) {
      setChartError("Seating map is not configured for this event yet. Please contact support.");
      return;
    }
    if (!scriptReady || !containerRef.current) return;

    if (chartRef.current) {
      try { chartRef.current.destroy(); } catch (_) {}
      chartRef.current = null;
    }
    setSelectedSeats([]);
    setHoldToken(null);
    setChartError(null);

    const containerId = "seating-chart-container";
    containerRef.current.id = containerId;

    const fallbackTicket = tickets[0] || null;

    try {
      chartRef.current = new window.seatsio.SeatingChart({
        divId: containerId,
        workspaceKey: SEATSIO_PUBLIC_KEY,
        event: seatingEventKey,
        pricing,
        session: "continue",
        showLegend: true,
        showMinimap: true,
        onObjectSelected: (seat) => {
          const categoryLabel = seat.category?.label || "";
          const seatPrice = seat.pricing?.price;
          const ticket =
            ticketByCategory[categoryLabel.toLowerCase()] ||
            (seatPrice != null ? tickets.find((t) => t.price === seatPrice) : null) ||
            fallbackTicket;
          const price = seat.pricing?.price ?? (ticket?.price || 0);
          setSelectedSeats((prev) => [
            ...prev,
            {
              label: seat.id,
              categoryLabel,
              price,
              currency: ticket?.currency || currency,
              ticket_id: ticket?.id || null,
            },
          ]);
        },
        onChartRendered: (chart) => {
          const ht = (chart || chartRef.current)?.holdToken;
          if (ht) setHoldToken(ht.token ?? ht);
        },
        onHoldSucceeded: () => {
          const ht = chartRef.current?.holdToken;
          if (ht) setHoldToken(ht.token ?? ht);
        },
        onObjectDeselected: (seat) => {
          setSelectedSeats((prev) => {
            const idx = prev.findIndex((s) => s.label === seat.id);
            if (idx === -1) return prev;
            return [...prev.slice(0, idx), ...prev.slice(idx + 1)];
          });
        },
        onHoldTokenExpired: () => {
          setSelectedSeats([]);
          setHoldToken(null);
        },
      });
      chartRef.current.render();
    } catch (err) {
      setChartError("Failed to initialize seating chart.");
      console.error(err);
    }

    return () => {
      if (chartRef.current) {
        try { chartRef.current.destroy(); } catch (_) {}
        chartRef.current = null;
      }
    };
  }, [open, scriptReady, seatingEventKey]);

  const handleProceed = () => {
    if (!selectedSeats.length || !holdToken) return;
    const hasUnmatchedTicket = selectedSeats.some((s) => !s.ticket_id);
    if (hasUnmatchedTicket) {
      console.warn("Some seats could not be matched to a ticket type by category/price.");
    }
    setOpen(false);
    navigate("/payment-summary", {
      state: {
        isSeating: true,
        eventData: data,
        selectedSeats,
        holdToken,
        currency,
        totalPrice,
      },
    });
  };

  return (
    <Modal open={open} sx={{ display: "flex", alignItems: "flex-start", justifyContent: "center", overflowY: "auto", pt: 2 }}>
      <Box
        sx={{
          background: COLORS.dark,
          border: "1px solid rgba(255,255,255,0.08)",
          borderRadius: RADIUS.modal,
          width: { xs: "100%", md: "90vw" },
          maxWidth: "1200px",
          minHeight: "80vh",
          display: "flex",
          flexDirection: "column",
          position: "relative",
          overflow: "hidden",
          boxShadow: "0 40px 120px rgba(0,0,0,0.6)",
        }}
      >
        {/* Header */}
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", p: "16px 24px", borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
          <Typography sx={{ color: COLORS.white, fontSize: "20px", fontWeight: 700, fontFamily: "'Playfair Display', serif" }}>
            {t("marketing.ticketFlow.chooseYourSeats")}
          </Typography>
          <Box sx={{ cursor: "pointer", color: COLORS.white, "&:hover": { color: COLORS.gold } }} onClick={() => setOpen(false)}>
            <RxCross2 size={22} />
          </Box>
        </Box>

        {/* Chart area */}
        <Box sx={{ flex: 1, p: "16px", minHeight: "500px", position: "relative" }}>
          {chartError ? (
            <Typography sx={{ color: "#f44336", textAlign: "center", mt: 4 }}>{chartError}</Typography>
          ) : !scriptReady ? (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "400px" }}>
              <CircularProgress sx={{ color: COLORS.gold }} />
            </Box>
          ) : null}
          <Box ref={containerRef} sx={{ width: "100%", height: "500px", display: scriptReady && !chartError ? "block" : "none" }} />
        </Box>

        {/* Bottom bar */}
        <Box
          sx={{
            borderTop: "1px solid rgba(255,255,255,0.08)",
            p: "16px 24px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            background: COLORS.gray,
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Box>
            <Typography sx={{ color: "rgba(255,255,255,0.6)", fontSize: "13px", fontFamily: fontFamilyFor(language) }}>
              {selectedSeats.length}{" "}
              {selectedSeats.length === 1
                ? t("marketing.ticketFlow.seat")
                : t("marketing.ticketFlow.seats")}{" "}
              {t("marketing.ticketFlow.selected")}
            </Typography>
            {selectedSeats.length > 0 && (
              <Typography sx={{ color: COLORS.gold, fontSize: "18px", fontWeight: 700, fontFamily: "'Playfair Display', serif" }}>
                {totalPrice} {currencyJson?.[currency]?.[language]}
              </Typography>
            )}
          </Box>
          <Button
            variant="contained"
            disabled={!selectedSeats.length || !holdToken}
            onClick={handleProceed}
            sx={{
              background: COLORS.peach,
              color: COLORS.dark,
              fontFamily: fontFamilyFor(language),
              fontWeight: 700,
              px: 4,
              py: 1.2,
              borderRadius: RADIUS.pill,
              boxShadow: "0 12px 34px rgba(212,175,55,0.25)",
              "&:hover": { background: COLORS.gold },
              "&:disabled": { background: "rgba(255,255,255,0.15)", color: "rgba(255,255,255,0.4)" },
            }}
          >
            {t("marketing.ticketFlow.continue")}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default SeatingMap;
